import { RouterProvider } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuthState } from "../api/queries.js";
import { accountLanguage } from "../i18n/index.js";
import { CodePage, SetupPage, SignInPage } from "../pages/auth.js";
import { AcceptInvitePage, INVITE_PATH } from "../pages/invite.js";
import { ErrorMessage } from "../ui/feedback.js";
import { Spinner } from "../ui/primitives.js";
import { UpdatePrompt } from "./update-prompt.js";
import { makeRouter } from "./router.js";

/** Chooses between an invitation, first-run setup, sign-in steps and the signed-in app. */
export function App() {
  const { t, i18n } = useTranslation();
  const state = useAuthState();
  // An invitation link (`/invite#<token>`) shows its own screen before anything else.
  const [inviting, setInviting] = useState(() => window.location.pathname === INVITE_PATH);
  const me = state.data?.me ?? null;
  // A new router per signed-in user; its context carries `me`.
  const router = useMemo(() => (me ? makeRouter(me) : null), [me]);

  useEffect(() => {
    const language = accountLanguage(me, i18n.resolvedLanguage);
    if (language) void i18n.changeLanguage(language);
  }, [me, i18n]);

  let screen;
  if (state.isPending) screen = <Spinner label={t("common.loading")} />;
  else if (state.error)
    screen = (
      <div className="p-6">
        <ErrorMessage error={state.error} />
      </div>
    );
  else if (inviting)
    screen = (
      <AcceptInvitePage
        signedIn={state.data.session !== "none"}
        onDone={() => {
          window.history.replaceState(null, "", "/");
          setInviting(false);
        }}
      />
    );
  else if (state.data.bootstrapNeeded)
    screen = <SetupPage codeRequired={state.data.bootstrapCodeRequired} />;
  else if (state.data.session === "needs_second_factor") screen = <CodePage />;
  else if (!router) screen = <SignInPage />;
  else screen = <RouterProvider router={router} />;

  return (
    <>
      {screen}
      <UpdatePrompt />
    </>
  );
}
